import { useState, useEffect } from 'react'
import { api } from '../lib/api'

interface Props {
  companyId: string
  companyName: string
}

interface Analysis {
  id: string
  status: 'pending' | 'running' | 'complete' | 'error'
  report: string | null
  sources: string | null
  founded_year: string | null
  headquarters: string | null
  employee_count: string | null
  is_public: number
  ticker_symbol: string | null
  revenue_estimate: string | null
  updated_at: string
}

export default function CompanyAnalysis({ companyId, companyName }: Props) {
  const [analysis, setAnalysis] = useState<Analysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)

  useEffect(() => {
    api.getCompanyAnalysis(companyId)
      .then(data => setAnalysis(data))
      .finally(() => setLoading(false))
  }, [companyId])

  const runResearch = async () => {
    setRunning(true)
    try {
      const result = await api.runCompanyResearch(companyId)
      setAnalysis(result)
    } catch (e: any) {
      alert('Research failed: ' + e.message)
    } finally {
      setRunning(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
      </div>
    )
  }

  if (!analysis || analysis.status === 'error') {
    return (
      <div className="card bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
        <div className="text-center py-8">
          <div className="text-4xl mb-3">🔍</div>
          <h3 className="text-lg font-semibold text-indigo-800 mb-2">AI Intelligence Report</h3>
          <p className="text-sm text-indigo-600 mb-4 max-w-md mx-auto">
            Run a comprehensive web search analysis on <strong>{companyName}</strong> to get
            company age, employee count, IPO status, social media activity, recent news, and more.
          </p>
          {analysis?.status === 'error' && analysis.report && (
            <p className="text-sm text-red-600 mb-3">Previous attempt failed: {analysis.report}</p>
          )}
          <button
            onClick={runResearch}
            disabled={running}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-all"
          >
            {running ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Researching...
              </span>
            ) : '🚀 Run AI Research'}
          </button>
          {running && (
            <p className="text-xs text-indigo-500 mt-3">
              This may take 15-30 seconds while we search the web...
            </p>
          )}
        </div>
      </div>
    )
  }

  if (analysis.status === 'running') {
    return (
      <div className="card text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3" />
        <p className="text-indigo-600 font-medium">Research in progress...</p>
        <p className="text-sm text-gray-500 mt-1">Searching the web for {companyName}</p>
      </div>
    )
  }

  let sources: string[] = []
  if (analysis.sources) {
    try { sources = JSON.parse(analysis.sources) } catch { /* plain string, skip */ }
  }

  return (
    <div className="space-y-4">
      {/* Quick Facts */}
      <div className="card bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-indigo-800 flex items-center gap-2">
            📊 Quick Facts
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Updated {new Date(analysis.updated_at).toLocaleDateString()}
            </span>
            <button
              onClick={runResearch}
              disabled={running}
              className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {analysis.founded_year && (
            <FactCard label="Founded" value={analysis.founded_year} icon="📅" />
          )}
          {analysis.headquarters && (
            <FactCard label="HQ" value={analysis.headquarters} icon="📍" />
          )}
          {analysis.employee_count && (
            <FactCard label="Employees" value={analysis.employee_count} icon="👥" />
          )}
          <FactCard
            label="Status"
            value={analysis.is_public ? `Public${analysis.ticker_symbol ? ` (${analysis.ticker_symbol})` : ''}` : 'Private'}
            icon={analysis.is_public ? '📈' : '🔒'}
          />
          {analysis.revenue_estimate && (
            <FactCard label="Revenue" value={analysis.revenue_estimate} icon="💰" />
          )}
        </div>
      </div>

      {/* Full Report */}
      {analysis.report && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              📋 Full Intelligence Report
            </h3>
            <button
              onClick={() => navigator.clipboard.writeText(analysis.report || '')}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              📋 Copy
            </button>
          </div>
          <div
            className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(analysis.report) }}
          />
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <div className="card bg-gray-50">
          <h3 className="font-semibold text-sm text-gray-600 mb-2">
            📎 Sources ({sources.length})
          </h3>
          <ul className="space-y-1">
            {sources.map((url, i) => (
              <li key={i} className="text-xs">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {url.length > 80 ? url.slice(0, 80) + '...' : url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function FactCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="bg-white rounded-lg p-3 border border-indigo-100">
      <div className="text-lg mb-1">{icon}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-medium text-gray-800 mt-0.5">{value}</div>
    </div>
  )
}

function renderMarkdown(md: string): string {
  return md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-2">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mt-5 mb-2 text-indigo-800">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-6 mb-3 text-indigo-900">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 list-decimal">$2</li>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>')
}
